import { applySchema } from './apply-schema';
import { normalizeCallBehavior } from './normalize-call-behavior';
import { normalizeErrorReporting } from './normalize-error-reporting';
import { z } from 'zod/v4';
import { Observable, pipe, UnaryFunction } from 'rxjs';

/**
 * Selects the data out of a Fetch API Response. All errors are reported using the
 * Fetch API's Response type. A timeout Response is reported if the service doesn't
 * respond within one minute. This is the complement to `ignoreData`.
 *
 * Intended to be used with `fromFetch` and Zod.
 *
 * Example use:
 * ```
 * fromFetch('url')
 *  .pipe(selectData(schema))
 *  .subscribe({
 *    next: (data) => {
 *      // Use `data`. `data` will get its type from `schema`.
 *    },
 *    error: (error: Response) => {
 *       // Report error.
 *       // Okay to use `error.statusText` and `error.status`.
 *       // More details may be in the error body.
 *    },
 *  });
 * ```
 *
 */
export function selectData<T extends z.ZodTypeAny>(
  schema: T
): UnaryFunction<Observable<Response>, Observable<z.output<T>>> {
  return pipe(
    normalizeCallBehavior(),
    applySchema(schema),
    normalizeErrorReporting()
  );
}
